import { ContentManagerModule, Context } from '@2sic.com/sxc-angular';
import { Component, computed, inject, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DownloadApiService } from '../apiCalls/downloads-api.service';
import { ComponentsComponent } from './components/components.component';
import { Components, DownloadCategory, LegacyDrivers } from './downloadsModel';
import { LegacyDriverComponent } from './legacy-driver/legacy-driver.component';
import { SimpleDownloadComponent } from './simple-download/simple-download.component';
import { SoftwareComponent } from './software/software.component';

@Component({
  selector: 'app-downloads',
  imports: [
    MatProgressSpinnerModule,
    ComponentsComponent,
    SimpleDownloadComponent,
    SoftwareComponent,
    ContentManagerModule,
    LegacyDriverComponent,
    MatCardModule,
  ],
  templateUrl: './downloads.component.html',
  styleUrl: './downloads.component.scss',
})
export class DownloadsComponent {
  downloadCategory = DownloadCategory;

  private readonly context = inject(Context);
  isEditMode = signal(this.context.sxc?.isEditMode());

  private readonly downloadDataSvc = inject(DownloadApiService);
  private readonly sxcContext = inject(Context);

  private readonly downloadData = this.downloadDataSvc.getDownload();

  /**
   * Computed property that extends the original downloadData object
   * by adding DownloadUrl properties to all relevant nested arrays (Drivers, Software, etc.).
   * Uses the helper mapping functions for a clean and consistent structure.
   */
  downloadDataExtended = computed(() => {
    const downloadData = this.downloadData();
    const edition = this.sxcContext.apiEdition;
    const sxcUrl = `/desktopmodules/2sxc/api/app/SpeedgoatExtranet/${edition}/api/Downloads/DownloadFile?`;

    if (!downloadData) return null;

    return {
      ...downloadData,
      IntroductionDrivers: this.mapWithDownloadUrl(downloadData.IntroductionDrivers, sxcUrl),
      CurrentReleaseDrivers: this.mapWithDownloadUrl(downloadData.CurrentReleaseDrivers, sxcUrl),
      OlderReleaseDrivers: this.mapWithDownloadUrl(downloadData.OlderReleaseDrivers, sxcUrl),
      Software: this.mapWithDownloadUrl(downloadData.Software, sxcUrl),
      Support: this.mapWithDownloadUrl(downloadData.Support, sxcUrl),
      LegacyDrivers: this.mapLegacyDrivers(downloadData.LegacyDrivers, sxcUrl),
      Components:
        downloadData.Components?.map((comp: Components) => ({
          ...comp,
          Files: this.mapWithDownloadUrl(comp.Files, sxcUrl),
        })) ?? [],
    };
  });

  categoriesData = this.downloadDataSvc.getCategories();

  getCategoryInfoByTitle(title: string) {
    return this.categoriesData()!.find((category) => category.Title === title);
  }

  /**
   * Returns the download URL for a given item.
   * - If the item has a 'File' property, returns its value directly.
   * - Otherwise, if both 'FileName' and 'FolderName' exist, builds a URL using these properties.
   * - Returns undefined if neither condition is met.
   */
  private getDownloadUrl(item: { File?: string; FileName?: string; FolderName?: string }, sxcUrl: string): string | undefined {
    if (item.File) return item.File;
    if (item.FileName && item.FolderName) return `${sxcUrl}folderName=${item.FolderName}&fileName=${item.FileName}`;
    return undefined;
  }

  /**
   * Maps an array of items, adding a calculated DownloadUrl to each item.
   * Uses getDownloadUrl for URL generation.
   */
  private mapWithDownloadUrl<T extends { File?: string; FileName?: string; FolderName?: string }>(arr: T[], sxcUrl: string): (T & { DownloadUrl?: string })[] {
    return (
      arr?.map((item) => ({
        ...item,
        DownloadUrl: this.getDownloadUrl(item, sxcUrl),
      })) ?? []
    );
  }

  /**
   * Maps legacy driver groups, and for each group maps its Driver array,
   * adding DownloadUrl to every driver using mapWithDownloadUrl.
   */
  private mapLegacyDrivers(groups: LegacyDrivers[], sxcUrl: string): LegacyDrivers[] {
    return (
      groups?.map((group) => ({
        ...group,
        Driver: this.mapWithDownloadUrl(group.Driver, sxcUrl),
      })) ?? []
    );
  }
}
